import { useCallback, useEffect, useState } from 'react';
import { pmGet, pmPost, type PmProject } from '../api';
import './ProjectCompletion.css';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Project Closure.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/project-completion.ejs`.
 *
 * WHAT IT DOES, in the source's words: formally close out a delivery project to an
 * enterprise standard — confirm the closure checklist across every domain, capture formal
 * approvals with a decision and date, hand over outstanding items, reconcile financials,
 * record benefits and the PIR, then generate the closure report.
 *
 * ─── A SOURCE DEFECT, FIXED AND LOGGED ───────────────────────────────────────
 *
 * `project-completion.ejs:295` reads
 *
 *     document.getElementById('kpiProgress').textContent = …
 *
 * but NO element carries `id="kpiProgress"` — the Delivery Progress card's heading
 * (`:111`) has no id at all. `getElementById` returns null, the assignment throws, and the
 * throw happens BEFORE `renderChecklist()`, `renderSignoffs()`, `renderOpenItems()`,
 * `updateBudget()`, `updateReadiness()` and `body.style.display = ''`. So in the source,
 * picking a project renders nothing: the whole screen stays hidden.
 *
 * Porting that faithfully would mean shipping a screen that never appears. The intent is
 * unambiguous — the card is captioned "Delivery Progress" and the value it wants is
 * `summary.overall_progress_pct` — so the value is rendered and the defect is recorded in
 * `docs/DEVIATIONS.md`. Raised with the owner rather than fixed silently (CLAUDE.md §1.2.6).
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * APPROVALS SAVE ON CHANGE, one row at a time, and the source re-reads the project
 * afterwards purely to pick up the server-stamped `signed_date`. That round trip is kept:
 * the date is the server's to decide, not the browser's.
 *
 * OUTSTANDING ITEMS SAVE ON EVERY EDIT — the source posts the whole list on each field
 * change, so there is no separate save for that table. Kept as-is.
 *
 * `M.toast(...)` becomes an inline status line carrying the same text and markers, which is
 * what the ported Settings screens in this module do.
 */

interface ChecklistRow {
  group: string;
  label: string;
  done: boolean;
}

/**
 * One initiative on this project, with its completion state and work-item rollup.
 *
 * Served by `/project-completion/data`, which gets it from the same rollup the Execution
 * Control Center reads — one source, so the two screens cannot disagree about whether an
 * initiative is finished.
 */
interface Initiative {
  opportunity_id: string;
  name: string;
  status: string;
  closed: boolean;
  completed_at: string | null;
  completed_by_name: string | null;
  closure_note: string | null;
  work_items_total: number;
  work_items_done: number;
  progress_pct: number;
  all_done: boolean;
  no_work_items: boolean;
}

interface Signoff {
  role: string;
  title: string;
  signer_name: string;
  signer_email: string;
  decision: string;
  conditions: string;
  signed: boolean;
  signed_date: string | null;
  comment: string;
}

interface OpenItem {
  item: string;
  item_type: string;
  owner: string;
  target_date: string;
  transition_to: string;
  status: string;
}

interface Completion {
  closure_type?: string | null;
  status?: string | null;
  planned_completion_date?: string | null;
  completion_date?: string | null;
  budget_planned?: number | null;
  budget_actual?: number | null;
  financial_notes?: string | null;
  benefits_owner?: string | null;
  pir_date?: string | null;
  benefits_summary?: string | null;
  final_summary?: string | null;
  lessons_learned?: string | null;
  checklist?: { group?: string; label: string; done?: boolean }[];
}

interface Summary {
  overall_progress_pct?: number;
  deliverables_done?: number;
  deliverables_total?: number;
}

/* The source's own lists, verbatim. */
const DECISIONS = ['Pending', 'Approved', 'Approved with conditions', 'Rejected'];
const ITEM_TYPES = ['Risk', 'Issue', 'Defect', 'Action'];
const TRANSITIONS = ['BAU', 'Support', 'Vendor', 'PMO'];
const OI_STATUS = ['Open', 'In Progress', 'Transferred', 'Closed'];
const CHECKLIST_GROUPS = [
  ['Scope & Deliverables', 'Scope & Work Items'],
  ['Risks & Issues', 'Risks & Issues'],
  ['Financial', 'Financial'],
  ['Resources & Transition', 'Resources & Transition'],
  ['Knowledge & Documentation', 'Knowledge & Documentation'],
  ['Benefits & Governance', 'Benefits & Governance'],
  ['General', 'General'],
];

const chipClass = (d: string) =>
  d === 'Approved'
    ? 'approved'
    : d === 'Approved with conditions'
      ? 'conditions'
      : d === 'Rejected'
        ? 'rejected'
        : 'pending';

const d10 = (v?: string | null) => (v ? String(v).slice(0, 10) : '');

type Toast = { kind: 'ok' | 'err' | 'warn'; text: string } | null;

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/project-completion.ejs`,
 * its `projectCompletionHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const PCOMP_HELP: HelpContent = {
    title: 'Project Closure',
    icon: '🏁',
    purpose: "Formally <strong>close out a delivery project</strong> to an enterprise standard: confirm the <strong>closure checklist</strong> across every domain, capture <strong>formal approvals</strong> (PM, Sponsor, Business Owner and each Steering Committee member) with a decision &amp; date, hand over <strong>outstanding items</strong>, reconcile <strong>financials</strong>, record <strong>benefits &amp; the PIR</strong>, then generate the <strong>closure report</strong>.",
    doHere: [
      "Pick the delivery project to close and set its <strong>closure type</strong> &amp; dates.",
      "Check <strong>Initiatives in this Project</strong> first. Close each one right here with <strong>Close</strong> &mdash; available once all of its work items are done &mdash; or on the <strong>Execution Control Center</strong>. Any that are still open do <strong>not</strong> block you: you will be warned, asked to confirm, and the open initiatives are recorded on the closure record.",
      "Work the <strong>closure checklist</strong> (grouped by domain) and clear or transfer <strong>outstanding items</strong>.",
      "Capture each <strong>approval</strong> &mdash; decision (Approve / with conditions / Reject) and date; add Steering Committee members as needed.",
      "Reconcile <strong>financials</strong>, record <strong>benefits &amp; PIR</strong>, write the <strong>summary &amp; lessons</strong>, set status to <em>Completed</em>, then <strong>generate the closure report</strong>."
    ],
    fits: 'Plan &rarr; Execute &rarr; <strong>Close</strong> &mdash; the final, governed step of delivery',
    firstTime: "Select your project; the checklist and standard approver roster are pre-filled so you can start closing out immediately. Start at <strong>Initiatives in this Project</strong> &mdash; the project cannot be completed until every one of them is marked complete.",
    sections: [
      {
        label: 'Initiatives must be complete before the project can be',
        type: 'html',
        html: "A project is only really finished when the <strong>initiatives inside it</strong> are, so this screen lists them and tells you which are still outstanding.<br><br>An initiative that is not complete <strong>blocks</strong> the project. Setting <em>Closure Status</em> to <strong>Completed</strong> is refused while any opportunity is not complete, and the message names exactly which ones. Everything else on this screen still saves in the meantime, and <em>In Progress</em> and <em>In Closure</em> are never blocked.<br><br>Only a <strong>project</strong> is ever closed. An opportunity is <strong>complete</strong> or <strong>not complete</strong> &mdash; nothing more, and nothing the system decides for you. <strong>Mark complete</strong> is offered once every one of its in-scope work items is at 100%; <strong>Mark not complete</strong> reverses it and puts the initiative back to <em>In Progress</em>. Both land in the <strong>Audit Trail</strong>."
      }
    ],
    links: [
      { label: '← Back: Project Management', href: '/project_management/program-management/action-needed' }
    ]
  };

export function ProjectCompletion() {
  const [projects, setProjects] = useState<PmProject[]>([]);
  const [projectId, setProjectId] = useState('');
  const [current, setCurrent] = useState<PmProject | null>(null);
  const [summary, setSummary] = useState<Summary>({});
  const [form, setForm] = useState<Completion>({});
  const [checklist, setChecklist] = useState<ChecklistRow[]>([]);
  const [signoffs, setSignoffs] = useState<Signoff[]>([]);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [openItems, setOpenItems] = useState<OpenItem[]>([]);
  const [newItem, setNewItem] = useState('');
  const [newGroup, setNewGroup] = useState<string>(CHECKLIST_GROUPS[0]![0]!);
  const [newRole, setNewRole] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [toast, setToast] = useState<Toast>(null);

  useEffect(() => {
    void (async () => {
      try {
        const d = await pmGet<{ success: boolean; error?: string; projects: PmProject[] }>(
          '/project-completion/projects',
        );
        if (!d.success) {
          setToast({ kind: 'err', text: `❌ ${d.error || 'Failed'}` });
          return;
        }
        setProjects(d.projects ?? []);
      } catch (e) {
        setToast({ kind: 'err', text: `❌ ${(e as Error).message}` });
      }
    })();
  }, []);

  const loadData = useCallback(async (id: string) => {
    try {
      const d = await pmGet<{
        success: boolean;
        error?: string;
        project: PmProject;
        summary?: Summary;
        completion: Completion;
        signoffs?: Partial<Signoff>[];
        open_items?: Partial<OpenItem>[];
        initiatives?: Initiative[];
        can_complete?: boolean;
        open_initiatives?: string[];
      }>(`/project-completion/data?delivery_project_id=${encodeURIComponent(id)}`);
      if (!d.success) {
        setToast({ kind: 'err', text: `❌ ${d.error || 'Failed'}` });
        return;
      }
      const c = d.completion ?? {};
      setInitiatives(d.initiatives ?? []);
      setCurrent(d.project);
      setSummary(d.summary ?? {});
      setForm({
        closure_type: c.closure_type || 'Normal',
        status: c.status || 'In Progress',
        planned_completion_date: d10(c.planned_completion_date),
        completion_date: d10(c.completion_date),
        budget_planned: c.budget_planned ?? null,
        budget_actual: c.budget_actual ?? null,
        financial_notes: c.financial_notes || '',
        benefits_owner: c.benefits_owner || '',
        pir_date: d10(c.pir_date),
        benefits_summary: c.benefits_summary || '',
        final_summary: c.final_summary || '',
        lessons_learned: c.lessons_learned || '',
      });
      setChecklist(
        (c.checklist ?? []).map((x) => ({
          group: x.group || 'General',
          label: x.label,
          done: !!x.done,
        })),
      );
      setSignoffs(
        (d.signoffs ?? []).map((s) => ({
          role: s.role ?? '',
          title: s.title || '',
          signer_name: s.signer_name || '',
          signer_email: s.signer_email || '',
          decision: s.decision || 'Pending',
          conditions: s.conditions || '',
          signed: !!s.signed,
          signed_date: s.signed_date ?? null,
          comment: s.comment || '',
        })),
      );
      setOpenItems(
        (d.open_items ?? []).map((o) => ({
          item: o.item ?? '',
          item_type: o.item_type || 'Action',
          owner: o.owner || '',
          target_date: d10(o.target_date),
          transition_to: o.transition_to || 'BAU',
          status: o.status || 'Open',
        })),
      );
      setToast(null);
    } catch (e) {
      setToast({ kind: 'err', text: `❌ ${(e as Error).message}` });
    }
  }, []);

  const post = async <T,>(path: string, payload: unknown): Promise<T | null> => {
    try {
      return await pmPost<T>(`/project-completion${path}`, payload);
    } catch {
      setToast({ kind: 'err', text: '❌ Network error' });
      return null;
    }
  };

  /** Save one approver, then re-read so the server-stamped `signed_date` lands. */
  const saveSignoff = async (s: Signoff) => {
    if (!current) return;
    const d = await post<{ success: boolean }>('/signoff/save', {
      delivery_project_id: current.delivery_project_id,
      role: s.role,
      title: s.title,
      signer_name: s.signer_name,
      signer_email: s.signer_email,
      decision: s.decision,
      conditions: s.conditions,
      comment: s.comment,
    });
    if (d?.success) {
      setToast({ kind: 'ok', text: '✅ Approval saved' });
      // `loadDataQuiet` — pick up the stamped dates without disturbing anything else.
      try {
        const r = await pmGet<{ success: boolean; signoffs?: Partial<Signoff>[] }>(
          `/project-completion/data?delivery_project_id=${encodeURIComponent(current.delivery_project_id)}`,
        );
        if (r.success) {
          setSignoffs((cur) =>
            cur.map((x) => {
              const ns = (r.signoffs ?? []).find((n) => n.role === x.role);
              return ns ? { ...x, signed_date: ns.signed_date ?? null } : x;
            }),
          );
        }
      } catch {
        /* the source swallows this too */
      }
    }
  };

  const saveOpenItems = async (items: OpenItem[]) => {
    if (!current) return;
    const d = await post<{ success: boolean }>('/open-items/save', {
      delivery_project_id: current.delivery_project_id,
      items,
    });
    if (d?.success) setToast({ kind: 'ok', text: '✅ Items saved' });
  };

  const openInitiatives = initiatives.filter((i) => !i.closed);
  const canComplete = openInitiatives.length === 0;

  /**
   * Mark ONE initiative complete, or not complete.
   *
   * Posts to the same endpoint the Execution Control Center uses, so the two screens write
   * through one path and cannot diverge. `screen` is carried into the audit entry, which is
   * how the trail says where a completion was recorded.
   *
   * The 409 for "work items still open" and the one asking for a note are shown as they
   * come back from the server: it is the authority, and its message already names the
   * counts. Restating them here would be a second copy to keep in step.
   */
  const markInitiative = async (i: Initiative, outcome: 'Completed' | 'reopen') => {
    if (!current) return;
    let note: string | null = null;
    if (outcome === 'Completed' && i.no_work_items) {
      // Zero work items reads as 100% by vacuous truth, so the rollup cannot vouch for it.
      note = window.prompt(
        `"${i.name}" has no work items. Add a note saying what completing it means:`,
        '',
      );
      if (note == null || !note.trim()) return;
    }
    const r = await pmPost<{ success: boolean; error?: string; needs_note?: boolean }>(
      '/tracker/opportunity/close',
      {
        delivery_project_id: current.delivery_project_id,
        opportunity_id: i.opportunity_id,
        outcome,
        closure_note: note,
        screen: 'project-completion',
      },
    );
    if (!r.success) {
      setToast({ kind: 'err', text: `❌ ${r.error || 'Could not update the initiative'}` });
      return;
    }
    setToast({
      kind: 'ok',
      text: outcome === 'Completed' ? `✅ ${i.name} marked complete` : `${i.name} marked not complete`,
    });
    await loadData(current.delivery_project_id);
  };

  const save = async () => {
    if (!current) return;
    /*
     * Checked here so the message appears IMMEDIATELY rather than after the user has
     * committed to the action. The server enforces the same rule and is the authority —
     * this is about when the person finds out, not about whether the rule holds.
     */
    if (form.status === 'Completed' && !canComplete) {
      setToast({
        kind: 'err',
        text:
          `❌ This project cannot be completed yet. ${openInitiatives.length} initiative(s) ` +
          `are not complete: ${openInitiatives.map((i) => i.name).join(', ')}.`,
      });
      return;
    }
    const d = await post<{ success: boolean; error?: string }>('/save', {
      delivery_project_id: current.delivery_project_id,
      status: form.status,
      closure_type: form.closure_type,
      planned_completion_date: form.planned_completion_date || null,
      completion_date: form.completion_date || null,
      budget_planned: form.budget_planned,
      budget_actual: form.budget_actual,
      financial_notes: form.financial_notes,
      benefits_owner: form.benefits_owner,
      benefits_summary: form.benefits_summary,
      pir_date: form.pir_date || null,
      checklist,
      final_summary: form.final_summary,
      lessons_learned: form.lessons_learned,
    });
    setToast(
      d?.success
        ? { kind: 'ok', text: '✅ Closure saved' }
        : { kind: 'err', text: `❌ ${d?.error || 'Save failed'}` },
    );
  };

  /* ── Derived values, all as the source computes them ─────────────────── */

  const checkDone = checklist.filter((c) => c.done).length;
  const checkPct = checklist.length ? Math.round((checkDone * 100) / checklist.length) : 0;
  const approvedCount = signoffs.filter(
    (s) => s.decision === 'Approved' || s.decision === 'Approved with conditions',
  ).length;
  const openLeft = openItems.filter(
    (o) => o.status !== 'Closed' && o.status !== 'Transferred',
  ).length;

  const p = Number(form.budget_planned);
  const a = Number(form.budget_actual);
  const budgetOk =
    form.budget_planned != null &&
    form.budget_actual != null &&
    String(form.budget_planned) !== '' &&
    String(form.budget_actual) !== '' &&
    !Number.isNaN(p) &&
    !Number.isNaN(a);
  const variance = budgetOk ? a - p : 0;
  const variancePct = budgetOk && p ? Math.round((variance * 100) / p) : 0;
  const varianceSign = variance > 0 ? '+' : '';
  const varianceText = budgetOk
    ? `${variance > 0 ? '+' : ''}$${variance.toLocaleString()} (${varianceSign}${variancePct}%)`
    : '—';
  const budgetKpi = budgetOk
    ? `budget ${variance > 0 ? 'over' : 'under'} ${varianceSign}${variancePct}%`
    : 'budget variance —';

  const conds = [
    { ok: checkPct === 100, label: 'Checklist 100% complete' },
    {
      ok:
        signoffs.length > 0 &&
        signoffs.every(
          (s) => s.decision === 'Approved' || s.decision === 'Approved with conditions',
        ),
      label: 'All approvers signed off',
    },
    { ok: openLeft === 0, label: 'Outstanding items closed/transferred' },
    { ok: !!form.completion_date, label: 'Actual completion date set' },
    { ok: (summary.overall_progress_pct || 0) >= 100, label: 'Delivery progress 100%' },
  ];
  const met = conds.filter((c) => c.ok).length;
  const total = conds.length;
  const bannerClass = met === total ? ' ready' : met >= total - 1 ? '' : ' blocked';
  const bannerIcon = met === total ? '✅' : met >= total - 1 ? '🔵' : '⚠️';
  const bannerTitle =
    met === total ? 'Ready to formally close' : `${met} of ${total} closure conditions met`;
  const bannerSub =
    met === total
      ? 'All governance conditions satisfied — set status to Completed and generate the closure report.'
      : `Outstanding: ${conds.filter((c) => !c.ok).map((c) => c.label).join(' · ')}`;

  /* The checklist, grouped in first-seen order, as `renderChecklist` does. */
  const groups: { group: string; idxs: number[] }[] = [];
  checklist.forEach((c, i) => {
    let g = groups.find((x) => x.group === c.group);
    if (!g) {
      g = { group: c.group, idxs: [] };
      groups.push(g);
    }
    g.idxs.push(i);
  });

  const setField = (k: keyof Completion, v: string | number | null) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="pm-pcomp">
      <HelpPanel help={withFinalizedRule(PCOMP_HELP)} />
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Project Closure</h1>
            <p className="page-header__subtitle">
              Formally close out the project — checklist, approvals, transition, financials,
              benefits &amp; lessons learned
            </p>
          </div>
        </div>
      </div>

      {/* Project + closure header */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-content">
          <div className="row" style={{ marginBottom: 0 }}>
            <div className="col m4">
              <SelectField
                id="pc-project"
                label="Project"
                orientation="stacked"
                width="lg"
                placeholder={PROMPT.project}
                value={projectId}
                onChange={(v) => {
                  setProjectId(v);
                  if (v) void loadData(v);
                }}
                options={projects.map((pr) => ({
                  value: pr.delivery_project_id,
                  id: pr.delivery_project_id,
                  description: `${pr.description ?? ''}${pr.methodology ? ` (${pr.methodology})` : ''}`.trim(),
                }))}
              />
            </div>
            <div className="col m2">
              <label className="pe-label" htmlFor="pc-type">
                Closure Type
              </label>
              <select
                id="pc-type"
                className="pe-select"
                disabled={!current}
                value={form.closure_type ?? 'Normal'}
                onChange={(e) => setField('closure_type', e.target.value)}
              >
                <option value="Normal">Normal (delivered)</option>
                <option value="Early">Early / partial</option>
                <option value="Terminated">Terminated</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div className="col m2">
              <label className="pe-label" htmlFor="pc-status">
                Closure Status
              </label>
              <select
                id="pc-status"
                className="pe-select"
                disabled={!current}
                value={form.status ?? 'In Progress'}
                onChange={(e) => setField('status', e.target.value)}
              >
                <option value="In Progress">In Progress</option>
                <option value="In Closure">In Closure</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
            <div className="col m2">
              <label className="pe-label" htmlFor="pc-planned">
                Planned Completion
              </label>
              <input
                id="pc-planned"
                type="date"
                className="pe-select"
                disabled={!current}
                value={form.planned_completion_date ?? ''}
                onChange={(e) => setField('planned_completion_date', e.target.value)}
              />
            </div>
            <div className="col m2">
              <label className="pe-label" htmlFor="pc-actual">
                Actual Completion
              </label>
              <input
                id="pc-actual"
                type="date"
                className="pe-select"
                disabled={!current}
                value={form.completion_date ?? ''}
                onChange={(e) => setField('completion_date', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {current ? (
        <div>
          {/* Closure readiness banner */}
          <div className={`pe-banner${bannerClass}`}>
            <div className="pe-banner__icon">{bannerIcon}</div>
            <div>
              <div className="pe-banner__title">{bannerTitle}</div>
              <div className="pe-banner__sub">{bannerSub}</div>
            </div>
          </div>

          {/* KPI strip */}
          <div className="row" style={{ marginTop: 16 }}>
            <div className="col m3">
              <div className="card pe-kpi">
                <div className="card-content center-align">
                  <span className="grey-text">Delivery Progress</span>
                  {/* See the header note: the source targets an id that does not exist. */}
                  <h1 className="page-header__title">{summary.overall_progress_pct || 0}%</h1>
                  <span className="grey-text text-darken-1" style={{ fontSize: '.82rem' }}>
                    {summary.deliverables_done || 0} / {summary.deliverables_total || 0} work items
                  </span>
                </div>
              </div>
            </div>
            <div className="col m3">
              <div className="card pe-kpi">
                <div className="card-content center-align">
                  <span className="grey-text">Checklist Complete</span>
                  <h4 className="blue-text text-darken-2">{checkPct}%</h4>
                  <div className="progress" style={{ margin: '6px 0 0' }}>
                    <div className="determinate" style={{ width: `${checkPct}%` }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="col m3">
              <div className="card pe-kpi">
                <div className="card-content center-align">
                  <span className="grey-text">Approvals Obtained</span>
                  <h4 className="blue-text text-darken-2">
                    {approvedCount} / {signoffs.length}
                  </h4>
                  <span className="grey-text text-darken-1" style={{ fontSize: '.82rem' }}>
                    formal decisions
                  </span>
                </div>
              </div>
            </div>
            <div className="col m3">
              <div className="card pe-kpi">
                <div className="card-content center-align">
                  <span className="grey-text">Open Items</span>
                  <h4 className="blue-text text-darken-2">{openLeft}</h4>
                  <span className="grey-text text-darken-1" style={{ fontSize: '.82rem' }}>
                    {budgetKpi}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/*
            INITIATIVES IN THIS PROJECT — above Formal Approvals, because a project cannot be
            completed until every initiative in it is, so this is the first thing that has to
            be true.

            Before this, Project Completion closed a whole project and said nothing about the
            initiatives inside it: the screen did not mention them anywhere, and there was no
            way to see what was holding closure up.

            Completing is offered HERE as well as on the Execution Control Center, and that is
            deliberate: whoever hits the gate on this screen must be able to clear it here,
            rather than being sent to a screen they may not have permission to open.
          */}
          <div className="card">
            <div className="card-content">
              <span className="card-title">Initiatives in this Project</span>
              {initiatives.length === 0 ? (
                <p className="grey-text">This project has no initiatives.</p>
              ) : (
                <>
                  <table className="striped">
                    <thead>
                      <tr>
                        <th>Initiative</th>
                        <th style={{ width: 150 }}>Work items</th>
                        <th style={{ width: 150 }}>State</th>
                        <th style={{ width: 190 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {initiatives.map((i) => (
                        <tr key={i.opportunity_id}>
                          <td>
                            {i.name}
                            {i.closure_note ? (
                              <div className="grey-text" style={{ fontSize: '.8rem' }}>
                                {i.closure_note}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            {i.no_work_items ? (
                              <span className="grey-text">none</span>
                            ) : (
                              `${i.work_items_done} / ${i.work_items_total}`
                            )}
                          </td>
                          <td>
                            {/* "Complete" / "Not complete" — an initiative is never "closed". */}
                            <span className={i.closed ? 'green-text text-darken-2' : 'grey-text text-darken-1'}>
                              {i.closed ? 'Complete' : 'Not complete'}
                            </span>
                            {i.completed_at ? (
                              <div className="grey-text" style={{ fontSize: '.78rem' }}>
                                {i.completed_at}
                                {i.completed_by_name ? ` · ${i.completed_by_name}` : ''}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn-small"
                              onClick={() => markInitiative(i, i.closed ? 'reopen' : 'Completed')}
                            >
                              {i.closed ? 'Mark not complete' : 'Mark complete'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* The gate, stated before the user reaches Save rather than after. */}
                  <div
                    className="card-panel"
                    style={{
                      marginTop: 14,
                      background: canComplete ? '#e6f4ea' : '#feefc3',
                      color: canComplete ? '#0d652d' : '#7a4f01',
                    }}
                  >
                    {canComplete
                      ? 'Every initiative is complete — this project can be marked Completed.'
                      : `This project cannot be completed yet. ${openInitiatives.length} initiative(s) are not complete: ${openInitiatives
                          .map((i) => i.name)
                          .join(', ')}.`}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Formal approvals */}
          <div className="card">
            <div className="card-content">
              <span className="card-title" style={{ fontSize: '1.15rem' }}>
                Formal Approvals
              </span>
              <p className="pe-section-sub">
                Each approver records a decision and date. <strong>Administrators &amp;
                Strategic Analysts</strong> aside, closure requires every approver to{' '}
                <em>Approve</em> (or approve with conditions). Add Steering Committee members as
                needed.
              </p>
              <table style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th style={{ width: '20%' }}>Role / Title</th>
                    <th style={{ width: '15%' }}>Signer</th>
                    <th style={{ width: '16%' }}>Email</th>
                    <th style={{ width: '16%' }}>Decision</th>
                    <th style={{ width: '17%' }}>Conditions / Comment</th>
                    <th style={{ width: '10%' }}>Date</th>
                    <th style={{ width: '6%' }} />
                  </tr>
                </thead>
                <tbody>
                  {signoffs.map((s, i) => (
                    <tr key={s.role}>
                      <td>
                        <strong>{s.role}</strong>
                        {s.title ? (
                          <>
                            <br />
                            <span className="grey-text" style={{ fontSize: '.75rem' }}>
                              {s.title}
                            </span>
                          </>
                        ) : null}
                      </td>
                      <td>
                        <input
                          type="text"
                          className="pe-input"
                          value={s.signer_name}
                          onChange={(e) =>
                            setSignoffs((cur) =>
                              cur.map((x, xi) =>
                                xi === i ? { ...x, signer_name: e.target.value } : x,
                              ),
                            )
                          }
                          onBlur={() => void saveSignoff(signoffs[i]!)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="pe-input"
                          value={s.signer_email}
                          onChange={(e) =>
                            setSignoffs((cur) =>
                              cur.map((x, xi) =>
                                xi === i ? { ...x, signer_email: e.target.value } : x,
                              ),
                            )
                          }
                          onBlur={() => void saveSignoff(signoffs[i]!)}
                        />
                      </td>
                      <td>
                        <select
                          className="pe-mini"
                          value={s.decision}
                          onChange={(e) => {
                            const next = signoffs.map((x, xi) =>
                              xi === i ? { ...x, decision: e.target.value } : x,
                            );
                            setSignoffs(next);
                            void saveSignoff(next[i]!);
                          }}
                        >
                          {DECISIONS.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </select>
                        <div style={{ marginTop: 3 }}>
                          <span className={`pe-chip ${chipClass(s.decision)}`}>{s.decision}</span>
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="pe-input"
                          placeholder="conditions / comment"
                          value={s.conditions || s.comment}
                          onChange={(e) =>
                            setSignoffs((cur) =>
                              cur.map((x, xi) =>
                                xi === i ? { ...x, conditions: e.target.value } : x,
                              ),
                            )
                          }
                          onBlur={() => void saveSignoff(signoffs[i]!)}
                        />
                      </td>
                      <td>
                        <span className="grey-text" style={{ fontSize: '.78rem' }}>
                          {s.signed_date || '—'}
                        </span>
                      </td>
                      <td className="center-align">
                        <button
                          type="button"
                          className="pe-rm"
                          onClick={() => {
                            setSignoffs((cur) => cur.filter((_, xi) => xi !== i));
                            if (current)
                              void post('/signoff/delete', {
                                delivery_project_id: current.delivery_project_id,
                                role: s.role,
                              });
                          }}
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="row" style={{ margin: '10px 0 0' }}>
                <div className="col m4">
                  <input
                    type="text"
                    className="pe-mini"
                    placeholder="Role (e.g. Steering Committee — J. Doe)"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                  />
                </div>
                <div className="col m4">
                  <input
                    type="text"
                    className="pe-mini"
                    placeholder="Title (optional)"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                  />
                </div>
                <div className="col m4">
                  <button
                    type="button"
                    className="btn-flat"
                    onClick={() => {
                      const v = newRole.trim();
                      if (!v) return;
                      if (signoffs.some((s) => s.role === v)) {
                        setToast({ kind: 'warn', text: 'That role already exists' });
                        return;
                      }
                      const row: Signoff = {
                        role: v,
                        title: newTitle.trim(),
                        signer_name: '',
                        signer_email: '',
                        decision: 'Pending',
                        conditions: '',
                        signed: false,
                        signed_date: null,
                        comment: '',
                      };
                      setSignoffs((cur) => [...cur, row]);
                      setNewRole('');
                      setNewTitle('');
                      if (current) void saveSignoff(row);
                    }}
                  >
                    Add approver
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Closure checklist */}
          <div className="card">
            <div className="card-content">
              <span className="card-title" style={{ fontSize: '1.15rem' }}>
                Closure Checklist
              </span>
              <p className="pe-section-sub">
                Grouped by closure domain. Tick each item as it is verified.
              </p>
              <div style={{ marginTop: 8 }}>
                {groups.map((g) => (
                  <div key={g.group}>
                    <div className="pe-group-h">{g.group}</div>
                    {g.idxs.map((i) => (
                      <p style={{ margin: '2px 0' }} key={i}>
                        <label className="pe-chk">
                          <input
                            type="checkbox"
                            checked={checklist[i]!.done}
                            onChange={(e) =>
                              setChecklist((cur) =>
                                cur.map((c, ci) =>
                                  ci === i ? { ...c, done: e.target.checked } : c,
                                ),
                              )
                            }
                          />
                          <span>{checklist[i]!.label}</span>
                        </label>
                        <button
                          type="button"
                          className="pe-rm"
                          style={{ marginLeft: 10 }}
                          onClick={() =>
                            setChecklist((cur) => cur.filter((_, ci) => ci !== i))
                          }
                        >
                          ✕
                        </button>
                      </p>
                    ))}
                  </div>
                ))}
              </div>
              <div className="row" style={{ margin: '10px 0 0' }}>
                <div className="col m5">
                  <input
                    type="text"
                    className="pe-mini"
                    placeholder="Add a checklist item…"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                  />
                </div>
                <div className="col m4">
                  <select
                    className="pe-mini"
                    value={newGroup}
                    onChange={(e) => setNewGroup(e.target.value)}
                  >
                    {CHECKLIST_GROUPS.map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col m3">
                  <button
                    type="button"
                    className="btn-flat"
                    onClick={() => {
                      const v = newItem.trim();
                      if (!v) return;
                      setChecklist((cur) => [...cur, { group: newGroup, label: v, done: false }]);
                      setNewItem('');
                    }}
                  >
                    Add item
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Outstanding items / transition */}
          <div className="card">
            <div className="card-content">
              <span className="card-title" style={{ fontSize: '1.15rem' }}>
                Outstanding Items &amp; Transition
              </span>
              <p className="pe-section-sub">
                Risks, issues, defects or actions not fully closed — formally handed to BAU,
                support or a vendor with an owner and target date.
              </p>
              <table style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>Item</th>
                    <th style={{ width: '12%' }}>Type</th>
                    <th style={{ width: '16%' }}>Owner</th>
                    <th style={{ width: '13%' }}>Target</th>
                    <th style={{ width: '13%' }}>Transition&nbsp;to</th>
                    <th style={{ width: '12%' }}>Status</th>
                    <th style={{ width: '4%' }} />
                  </tr>
                </thead>
                <tbody>
                  {openItems.map((o, i) => {
                    const upd = (field: keyof OpenItem, val: string) => {
                      const next = openItems.map((x, xi) =>
                        xi === i ? { ...x, [field]: val } : x,
                      );
                      setOpenItems(next);
                      void saveOpenItems(next);
                    };
                    return (
                      <tr key={i}>
                        <td>
                          <input
                            type="text"
                            className="pe-input"
                            value={o.item}
                            onChange={(e) =>
                              setOpenItems((cur) =>
                                cur.map((x, xi) => (xi === i ? { ...x, item: e.target.value } : x)),
                              )
                            }
                            onBlur={(e) => upd('item', e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            className="pe-mini"
                            value={o.item_type}
                            onChange={(e) => upd('item_type', e.target.value)}
                          >
                            {ITEM_TYPES.map((v) => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="pe-input"
                            value={o.owner}
                            onChange={(e) =>
                              setOpenItems((cur) =>
                                cur.map((x, xi) =>
                                  xi === i ? { ...x, owner: e.target.value } : x,
                                ),
                              )
                            }
                            onBlur={(e) => upd('owner', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            className="pe-input"
                            value={o.target_date}
                            onChange={(e) => upd('target_date', e.target.value)}
                          />
                        </td>
                        <td>
                          <select
                            className="pe-mini"
                            value={o.transition_to}
                            onChange={(e) => upd('transition_to', e.target.value)}
                          >
                            {TRANSITIONS.map((v) => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            className="pe-mini"
                            value={o.status}
                            onChange={(e) => upd('status', e.target.value)}
                          >
                            {OI_STATUS.map((v) => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="center-align">
                          <button
                            type="button"
                            className="pe-rm"
                            onClick={() => {
                              const next = openItems.filter((_, xi) => xi !== i);
                              setOpenItems(next);
                              void saveOpenItems(next);
                            }}
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button
                type="button"
                className="btn-flat"
                onClick={() =>
                  setOpenItems((cur) => [
                    ...cur,
                    {
                      item: '',
                      item_type: 'Action',
                      owner: '',
                      target_date: '',
                      transition_to: 'BAU',
                      status: 'Open',
                    },
                  ])
                }
              >
                Add item
              </button>
            </div>
          </div>

          {/* Financial closure */}
          <div className="card">
            <div className="card-content">
              <span className="card-title" style={{ fontSize: '1.15rem' }}>
                Financial Closure
              </span>
              <p className="pe-section-sub">Reconcile final actuals against the approved budget.</p>
              <div className="row" style={{ marginBottom: 0 }}>
                <div className="col m3">
                  <label className="pe-label" htmlFor="pc-bp">
                    Budget — Planned
                  </label>
                  <input
                    id="pc-bp"
                    type="number"
                    className="pe-select"
                    placeholder="0"
                    step={1000}
                    value={form.budget_planned ?? ''}
                    onChange={(e) =>
                      setField('budget_planned', e.target.value === '' ? null : Number(e.target.value))
                    }
                  />
                </div>
                <div className="col m3">
                  <label className="pe-label" htmlFor="pc-ba">
                    Final Actual Cost
                  </label>
                  <input
                    id="pc-ba"
                    type="number"
                    className="pe-select"
                    placeholder="0"
                    step={1000}
                    value={form.budget_actual ?? ''}
                    onChange={(e) =>
                      setField('budget_actual', e.target.value === '' ? null : Number(e.target.value))
                    }
                  />
                </div>
                <div className="col m3">
                  <label className="pe-label">Variance</label>
                  <div
                    className="pe-select"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#f5f7fa',
                      color: budgetOk ? (variance > 0 ? '#c62828' : '#2e7d32') : undefined,
                    }}
                  >
                    {varianceText}
                  </div>
                </div>
                <div className="col m3">
                  <label className="pe-label" htmlFor="pc-fn">
                    Notes
                  </label>
                  <input
                    id="pc-fn"
                    type="text"
                    className="pe-select"
                    placeholder="POs closed, variance reason…"
                    value={form.financial_notes ?? ''}
                    onChange={(e) => setField('financial_notes', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Benefits & PIR */}
          <div className="card">
            <div className="card-content">
              <span className="card-title" style={{ fontSize: '1.15rem' }}>
                Benefits Realization &amp; Post-Implementation Review
              </span>
              <div className="row" style={{ margin: '8px 0 0' }}>
                <div className="col m6">
                  <label className="pe-label" htmlFor="pc-bo">
                    Benefits Owner (tracks value post-closure)
                  </label>
                  <input
                    id="pc-bo"
                    type="text"
                    className="pe-select"
                    placeholder="e.g. Business Owner / PMO"
                    value={form.benefits_owner ?? ''}
                    onChange={(e) => setField('benefits_owner', e.target.value)}
                  />
                </div>
                <div className="col m6">
                  <label className="pe-label" htmlFor="pc-pir">
                    Post-Implementation Review (PIR) date
                  </label>
                  <input
                    id="pc-pir"
                    type="date"
                    className="pe-select"
                    value={form.pir_date ?? ''}
                    onChange={(e) => setField('pir_date', e.target.value)}
                  />
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <label className="pe-label" htmlFor="pc-bs">
                  Expected benefits &amp; how they will be measured
                </label>
                <textarea
                  id="pc-bs"
                  className="pe-textarea"
                  maxLength={3000}
                  value={form.benefits_summary ?? ''}
                  onChange={(e) => setField('benefits_summary', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Narrative */}
          <div className="card">
            <div className="card-content">
              <span className="card-title" style={{ fontSize: '1.15rem' }}>
                Closure Narrative
              </span>
              <div style={{ marginTop: 8 }}>
                <label className="pe-label" htmlFor="pc-fs">
                  Final Summary — outcomes, scope delivered, residual items
                </label>
                <textarea
                  id="pc-fs"
                  className="pe-textarea"
                  maxLength={4000}
                  value={form.final_summary ?? ''}
                  onChange={(e) => setField('final_summary', e.target.value)}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                <label className="pe-label" htmlFor="pc-ll">
                  Lessons Learned — what went well, what to improve
                </label>
                <textarea
                  id="pc-ll"
                  className="pe-textarea"
                  maxLength={4000}
                  value={form.lessons_learned ?? ''}
                  onChange={(e) => setField('lessons_learned', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div style={{ margin: '8px 0 24px' }}>
            <button type="button" className="btn" onClick={() => void save()}>
              Save Closure
            </button>
            <button
              type="button"
              className="btn-flat"
              style={{ marginLeft: 8 }}
              onClick={() => {
                if (!current) {
                  setToast({ kind: 'warn', text: 'Pick a project first' });
                  return;
                }
                window.open(
                  `/api/project_management/project-completion/report?delivery_project_id=${encodeURIComponent(current.delivery_project_id)}`,
                  '_blank',
                );
              }}
            >
              Generate Closure Report
            </button>
            {toast ? <div className={`pm-toast ${toast.kind}`}>{toast.text}</div> : null}
          </div>
        </div>
      ) : null}

      {!current && toast ? <div className={`pm-toast ${toast.kind}`}>{toast.text}</div> : null}
    </div>
  );
}
